import { createFileRoute, Link } from "@tanstack/react-router";
import { ctrDesc, ctrTitle } from "@/lib/seo-copy";
import { NAATI_COMPARE } from "@/data/naati/compare";
import { absoluteUrl } from "@/config/sites";
import { Breadcrumbs } from "@/components/Breadcrumbs";
import { naatiCompareHubHreflangLinks } from "@/data/naati/hreflang";

export const Route = createFileRoute("/naati/compare/")({
  loader: () => {
    const url = absoluteUrl("/naati/compare");
    const title = "เทียบทางเลือกรับรองเอกสาร NAATI — เลือกให้ถูกตั้งแต่ครั้งแรก";
    const desc =
      "NAATI ต่างจากกงสุล โนตารี Apostille และนักแปลสาบานตนอย่างไร เทียบทีละข้อพร้อมเช็กลิสต์เลือกเส้นทางที่ตรงกับหน่วยงานปลายทางของคุณ";
    return {
      h: {
        meta: [
          { title: ctrTitle(title) },
          { name: "description", content: ctrDesc(desc) },
          { property: "og:title", content: ctrTitle(title) },
          { property: "og:description", content: ctrDesc(desc) },
          { property: "og:url", content: url },
          { property: "og:type", content: "website" },
          { name: "twitter:card", content: "summary_large_image" },
        ],
        links: [{ rel: "canonical", href: url }, ...naatiCompareHubHreflangLinks()],
        scripts: [
          {
            type: "application/ld+json",
            children: JSON.stringify({
              "@context": "https://schema.org",
              "@type": "ItemList",
              name: title,
              itemListElement: NAATI_COMPARE.map((c, i) => ({
                "@type": "ListItem",
                position: i + 1,
                name: c.h1,
                url: absoluteUrl(`/naati/compare/${c.slug}`),
              })),
            }),
          },
        ],
      },
    };
  },
  head: ({ loaderData }) => loaderData?.h ?? {},
  component: CompareHub,
});

function CompareHub() {
  return (
    <>
      <Breadcrumbs jsonLd
        items={[{ label: "หน้าแรก", to: "/" }, { label: "NAATI", to: "/naati" }, { label: "เทียบทางเลือก" }]}
      />
      <section className="mx-auto max-w-4xl px-4 py-10">
        <h1 className="text-3xl font-bold mb-4">เทียบทางเลือกการรับรองเอกสาร NAATI</h1>
        <p className="lead text-muted-foreground mb-8" data-speakable>
          คำตอบสั้น: NAATI รับรอง “คำแปล” ส่วนกงสุล โนตารี และ Apostille รับรอง “ตัวเอกสารหรือลายมือชื่อ”
          เลือกเส้นทางตามหน่วยงานปลายทาง ไม่ใช่ตามราคา เพื่อลดโอกาสถูกขอเอกสารเพิ่ม
        </p>
        <div className="grid gap-4 sm:grid-cols-2">
          {NAATI_COMPARE.map((c) => (
            <article key={c.slug} className="border rounded-xl p-5">
              <h2 className="font-semibold mb-2">
                <Link to="/naati/compare/$slug" params={{ slug: c.slug }} className="text-blue-700 hover:underline">
                  {c.h1}
                </Link>
              </h2>
              <p className="text-sm text-muted-foreground">{c.description}</p>
            </article>
          ))}
        </div>
        <nav className="mt-8 flex flex-wrap gap-4 text-sm">
          <Link to="/naati" className="text-blue-700 hover:underline">หน้าหลัก NAATI</Link>
          <Link to="/naati/questions" className="text-blue-700 hover:underline">คำถามที่พบบ่อย</Link>
          <Link to="/naati/price" className="text-blue-700 hover:underline">ค่าบริการ</Link>
        </nav>
      </section>
    </>
  );
}
